import { CheckOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card, Space, Typography } from "antd";
import React, { useState } from "react";
import {
  FixedIncomeRequestKyc,
  FixedIncomeRequest,
  Customer,
  SelectOptionsInterface,
  BranchMap,
  KycEnumOptions,
  FixedIncomeRequestKycBeneficiary,
} from "../helpers/shared-interfaces";
import { FixedIncomeRequestKycForm } from "./FixedIncomeRequestKycForm";
import { UploadFile } from "antd/lib/upload/interface";
import { FixedIncomeRequestKycView } from "./FixedIncomeRequestKycView";

const { Text } = Typography;

interface Props {
  lastKyc: FixedIncomeRequestKyc;
  fixedIncomeRequest: FixedIncomeRequest;
  customer: Customer;
  maritalStatuses: SelectOptionsInterface[];
  onConfirmReview: (kycData: any) => void;
  onCancel: () => void;
  isLoading?: boolean;
  handleFileUploadChange: (field: string, combinedInfo: string) => void;
  handleDelete: (file: UploadFile) => void;
  handleEmploymentStatusChange: (value: string) => void;
  handleResidentialStatusSelect: (value: string) => void;
  financialInstitutions: SelectOptionsInterface[];
  branchMap: BranchMap;
  payoutInstitutionOptions: SelectOptionsInterface[];
  payoutBranches: SelectOptionsInterface[];
  selectedPaymentMethod?: string;
  handlePaymentMethodSelect: (value: string) => void;
  selectedPayoutCurrency?: string;
  handlePayoutCurrencySelect: (value: string) => void;
  handlePayoutBankSelect: (value: string) => void;
  employmentStatus?: string;
  residentialStatus?: string;
  handleBeneficiaryTypeChange: (value: string) => void;
  beneficiaryType?: string;
  kycEnumOptions: KycEnumOptions;
  beneficiaries: FixedIncomeRequestKycBeneficiary[];
  onAddBeneficiary: () => void;
  onRemoveBeneficiary: (index: number) => void;
  onBeneficiaryFieldChange: (index: number, field: string, value: any) => void;
  kycDeclaration?: string;
  handleKycDeclarationChange?: (value: string) => void;
  handleStatementFrequencyChange?: (value: string) => void;
  selectedStatementFrequency?: string;
  handleInvestmentCurrencySelect?: (value: string) => void;
  selectedInvestmentCurrency?: string;
}

export const FixedIncomeRequestKycReview: React.FC<Props> = ({
  lastKyc,
  fixedIncomeRequest,
  customer,
  onConfirmReview,
  onCancel,
  isLoading = false,
  handleFileUploadChange,
  handleDelete,
  handleEmploymentStatusChange,
  handleResidentialStatusSelect,
  residentialStatus,
  handleBeneficiaryTypeChange,
  beneficiaryType,
  financialInstitutions,
  branchMap,
  payoutInstitutionOptions,
  payoutBranches,
  selectedPaymentMethod,
  handlePaymentMethodSelect,
  selectedPayoutCurrency,
  handlePayoutCurrencySelect,
  handlePayoutBankSelect,
  employmentStatus,
  kycEnumOptions,
  beneficiaries,
  onAddBeneficiary,
  onRemoveBeneficiary,
  onBeneficiaryFieldChange,
  kycDeclaration,
  handleKycDeclarationChange,
  handleStatementFrequencyChange,
  selectedStatementFrequency,
  handleInvestmentCurrencySelect
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleConfirmReview = () => {
    const kycData = {
      fixedIncomeRequestId: fixedIncomeRequest.id,
      ...lastKyc,
      customerId: customer.id,
    };
    onConfirmReview(kycData);
  };

  const handleEditSubmit = (kycData: any) => {
    onConfirmReview(kycData);
  };

  if (isEditing) {
    return (
      <div>
        <Card
          size="small"
          style={{
            marginBottom: 16,
            backgroundColor: "#e6f7ff",
            borderColor: "#1890ff",
          }}
        >
          <Space direction="vertical" size={4}>
            <Text strong style={{ color: "#1890ff" }}>
              Editing Previous KYC Information
            </Text>
            <Text type="secondary">
              Update any information that has changed since your last
              application
            </Text>
          </Space>
        </Card>

        <FixedIncomeRequestKycForm
          fixedIncomeRequest={{
            ...fixedIncomeRequest,
            fixedIncomeRequestKyc: lastKyc,
          }}
          onSubmit={handleEditSubmit}
          onCancel={() => setIsEditing(false)}
          isLoading={isLoading}
          customer={customer}
          isEditMode={true}
          handleFileUploadChange={handleFileUploadChange}
          handleDelete={handleDelete}
          handleEmploymentStatusChange={handleEmploymentStatusChange}
          handleResidentialStatusSelect={handleResidentialStatusSelect}
          financialInstitutions={financialInstitutions}
          branchMap={branchMap}
          handlePaymentMethodSelect={handlePaymentMethodSelect}
          selectedPaymentMethod={selectedPaymentMethod}
          handlePayoutCurrencySelect={handlePayoutCurrencySelect}
          selectedPayoutCurrency={selectedPayoutCurrency}
          payoutBranches={payoutBranches}
          handlePayoutBankSelect={handlePayoutBankSelect}
          payoutInstitutionOptions={payoutInstitutionOptions}
          employmentStatus={employmentStatus}
          residentialStatus={residentialStatus}
          handleBeneficiaryTypeChange={handleBeneficiaryTypeChange}
          beneficiaryType={beneficiaryType}
          kycEnumOptions={kycEnumOptions}
          beneficiaries={beneficiaries}
          onAddBeneficiary={onAddBeneficiary}
          onRemoveBeneficiary={onRemoveBeneficiary}
          onBeneficiaryFieldChange={onBeneficiaryFieldChange}
          kycDeclaration={kycDeclaration}
          handleKycDeclarationChange={handleKycDeclarationChange}
          handleStatementFrequencyChange={handleStatementFrequencyChange}
          selectedStatementFrequency={selectedStatementFrequency}
          handleInvestmentCurrencySelect={handleInvestmentCurrencySelect}
        />
      </div>
    );
  }

  return (
    <div>
      <Card
        size="small"
        style={{
          marginBottom: 16,
          backgroundColor: "#fff7e6",
          borderColor: "#ffa940",
        }}
      >
        <Space direction="vertical" size={4}>
          <Text strong style={{ color: "#fa8c16" }}>
            Review Previous KYC Information
          </Text>
          <Text type="secondary">
            This information was collected from your previous fixed income
            application. Please review and confirm if it's still accurate, or
            edit if needed.
          </Text>
        </Space>
      </Card>

      <div style={{ marginBottom: 24 }}>
        <FixedIncomeRequestKycView
          fixedIncomeRequest={{
            ...fixedIncomeRequest,
            fixedIncomeRequestKyc: lastKyc,
          }}
          onEdit={undefined}
        />
      </div>

      <Card size="small">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text type="secondary">Is this information still accurate?</Text>

          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
              Edit Information
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleConfirmReview}
              loading={isLoading}
            >
              Confirm & Use This Information
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
};
